import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { WhiteboardBus } from '../bus/whiteboard-bus.js';
import type { UiAction } from '../bus/ui-action.events.js';
import type { ObjectId } from '../bus/branded-types.js';
import { FSM } from '../fsm/fsm.js';
import { EDIT_TRANSITIONS, type EditEvent, type EditState } from '../fsm/edit.fsm.js';

/**
 * Lit ReactiveController owning the edit-FSM (TR15).
 *
 * Translates `edit/*` UiActions into FSM transitions and provides the cross-FSM
 * safety net: when `tool/set` arrives while the FSM is non-idle, the controller
 * re-emits `edit/cancel` with the tracked target-id (so slice editors close
 * their DOM surfaces) and drives the FSM back to idle. The re-emit is
 * idempotent because `FSM.canSend('cancel')` returns false from the idle state.
 *
 * FC11: bus stream errors are swallowed with a DEV-only console warning; the
 * controller does not propagate exceptions out of the subscription.
 */
export class EditModeController implements ReactiveController {
    readonly #host: ReactiveControllerHost & HTMLElement;
    readonly #bus: WhiteboardBus;
    readonly #disconnect$ = new Subject<void>();
    readonly #fsm = new FSM<EditState, EditEvent>('idle', EDIT_TRANSITIONS);

    #currentEditTargetId: ObjectId | null = null;
    #readonly = false;

    constructor(host: ReactiveControllerHost & HTMLElement, bus: WhiteboardBus) {
        this.#host = host;
        this.#bus = bus;
    }

    /** Current FSM state, exposed read-only for tests / status indicators. */
    get currentState(): EditState {
        return this.#fsm.current;
    }

    /**
     * PRD-X / TR6: toggle readonly mode. While active, `edit/enter-*` UiActions
     * are ignored so no new editor session can start. `edit/commit` and
     * `edit/cancel` remain functional so an in-flight editor can close cleanly
     * when readonly is enabled at runtime.
     */
    setReadonly(readonly: boolean): void {
        this.#readonly = readonly;
    }

    hostConnected(): void {
        this.#bus.uiActions$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({
                next: (action) => this.#handle(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[EditModeController] bus error swallowed');
                    }
                },
            });
        // Reference host to satisfy TS unused-field check; used for symmetry
        // with other slice controllers and future requestUpdate hooks.
        void this.#host;
    }

    hostDisconnected(): void {
        this.#disconnect$.next();
        this.#disconnect$.complete();
    }

    #handle(action: UiAction): void {
        switch (action.type) {
            case 'edit/enter-sticky':
                if (this.#readonly) return;
                if (this.#fsm.canSend('enter-sticky')) {
                    this.#fsm.send('enter-sticky');
                    this.#currentEditTargetId = action.id;
                }
                return;
            case 'edit/commit':
                if (this.#fsm.canSend('commit')) {
                    this.#fsm.send('commit');
                    this.#currentEditTargetId = null;
                }
                return;
            case 'edit/cancel':
                if (this.#fsm.canSend('cancel')) {
                    this.#fsm.send('cancel');
                    this.#currentEditTargetId = null;
                }
                return;
            case 'tool/set':
                this.#handleToolSet();
                return;
            default:
                return;
        }
    }

    /**
     * Cross-FSM safety: drive editor back to idle on tool change. Re-emits
     * `edit/cancel` so slice editors (e.g. StickyEditorController) close their
     * DOM. Idempotent — when the re-emitted `edit/cancel` returns through the
     * subscription, the FSM is already idle and `canSend('cancel')` is false.
     */
    #handleToolSet(): void {
        if (this.#fsm.current === 'idle') return;
        const id = this.#currentEditTargetId;
        this.#fsm.send('cancel');
        this.#currentEditTargetId = null;
        if (id !== null) {
            this.#bus.uiActions$.next({ type: 'edit/cancel', id });
        }
    }
}
